/**
 * Main Chat Widget Component
 */

import React, { useEffect, useRef } from 'react';
import { useChat } from '../hooks/useChat';
import { ChatWindow } from './ChatWindow';
import { MinimizedButton } from './MinimizedButton';
import { widgetBridge } from '../services/widgetBridge';
import type { WidgetConfig } from '../types';

interface ChatWidgetProps {
  config: WidgetConfig;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ config }) => {
  const { state, sendMessage, toggleWidget, openWidget, closeWidget, minimizeWidget, maximizeWidget } = useChat(config);

  // Keep refs in sync so bridge callbacks always see latest values
  const openRef = useRef(openWidget);
  openRef.current = openWidget;
  const closeRef = useRef(closeWidget);
  closeRef.current = closeWidget;
  const sendMessageRef = useRef(sendMessage);
  sendMessageRef.current = sendMessage;

  // Register bridge handlers once (refs ensure fresh values)
  useEffect(() => {
    widgetBridge.on('open', () => {
      openRef.current();
    });

    widgetBridge.on('close', () => {
      closeRef.current();
    });

    widgetBridge.on('sendMessage', (message: string) => {
      openRef.current();
      setTimeout(() => {
        sendMessageRef.current(message);
      }, 500);
    });

    return () => {
      widgetBridge.off('open');
      widgetBridge.off('close');
      widgetBridge.off('sendMessage');
    };
  }, []);

  return (
    <div
      id="hiva-webchat-root"
      className={config.theme?.position === 'bottom-left' ? 'bottom-left' : ''}
      style={{
        '--hiva-primary-color': config.theme?.primaryColor || '#0066FF',
        '--hiva-primary-hover': config.theme?.primaryColor ? `${config.theme.primaryColor}dd` : '#0052CC',
      } as React.CSSProperties}
    >
      {state.isOpen && !state.isMinimized ? (
        <ChatWindow
          messages={state.messages}
          isConnected={state.isConnected}
          isTyping={state.isTyping}
          agentName={state.agentName}
          onSendMessage={sendMessage}
          onMinimize={minimizeWidget}
          onClose={toggleWidget}
        />
      ) : (
        <MinimizedButton
          onClick={state.isOpen ? maximizeWidget : toggleWidget}
          unreadCount={0} // TODO: Implement unread count
        />
      )}
    </div>
  );
};
